import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { FileTransferConfig } from '@/api/media/configs';
import { StorageFields } from './StorageFields';
import { useStorageForm } from './use-storage-form';
import { STORAGE_KIND_OPTIONS, type StorageKind, type EditRow } from './storage-form-types';
import styles from './StorageFields.module.css';

interface FileTransferCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceId: string;
  config: FileTransferConfig;
  editRow?: EditRow;
  onDone: () => void;
}

/** Create or edit an output storage configuration. */
export function FileTransferCreateDialog({
  open,
  onOpenChange,
  serviceId,
  config,
  editRow,
  onDone,
}: FileTransferCreateDialogProps) {
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const { labels } = config;

  const handleDone = () => {
    onDone();
    onOpenChange(false);
  };

  const { activeType, currentDraft, currentErrors, isSubmitting, isEditMode, setActiveType, updateField, handleSubmit } =
    useStorageForm(serviceId, config, handleDone, editRow);

  const shownErrors = attemptedSubmit ? currentErrors : {};

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    void handleSubmit(e);
  };

  const title = isEditMode ? labels.editDialogTitle : labels.createDialogTitle;
  const description = isEditMode ? labels.editDialogDescription : labels.createDialogDescription;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <form id="storage-form" onSubmit={onSubmit} className={styles.formStack}>
            <Field label="Protocol">
              {(fp) => (
                <Select
                  value={activeType}
                  onValueChange={(v) => setActiveType(v as StorageKind)}
                  disabled={isEditMode}
                >
                  <SelectTrigger {...fp}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STORAGE_KIND_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <StorageFields
              type={activeType}
              draft={currentDraft}
              errors={shownErrors}
              onChange={updateField}
            />
          </form>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="storage-form"
            disabled={isSubmitting}
            onClick={() => setAttemptedSubmit(true)}
          >
            {isSubmitting ? 'Saving…' : isEditMode ? 'Save Changes' : 'Create Storage'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
